import React, { useState, createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import {
  createGrowthSession as createGrowthSessionAPI,
  updateGrowthSession as updateGrowthSessionAPI,
  deleteGrowthSession as deleteGrowthSessionAPI,
  getAllGrowthSessions as getAllGrowthSessionsAPI
} from '../Queries';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Container,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Fade,
  Skeleton,
  Avatar,
  Tooltip,
  Badge,
  Divider,
  Alert,
  Snackbar,
  LinearProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  alpha,
  Zoom,
  Grow,
  Stack,
  Pagination
} from '@mui/material';
// DataGrid imports removed - using CommonTable instead
import {
  Add as AddIcon,
  Edit as EditIcon,
  Feedback as FeedbackIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  Assignment as AssignmentIcon,
  Clear as ClearIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  Groups as GroupsIcon,
  ReportProblem as ReportProblemIcon,
  Psychology as PsychologyIcon,
  AddComment,
  Feedback,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  UnfoldMore as UnfoldMoreIcon,
  TrendingUp,
  History as HistoryIcon
} from '@mui/icons-material';
import StatCard from '../UI/StatCard';

import GrowthSessionDialog from './GrowthSessionDialog';
import ViewGrowthSessionDialog from './ViewGrowthSessionDialog';
import CommonHistoryDialog from './CommonHistoryDialog';
import PIPDialog from './PIPDialog';
import UserHierarchyDialog from './UserHierarchyDialog';
import LoadingScreen from '../UI/LoadingScreen';
import GrowthSessionTable from './GrowthSessionTable';
import UserTable from './UserTable';
import TabsLayout from '../Common/TabsLayout';
import { 
  getUsers, 
  getUser
} from '../Queries';
import {
  parseGrowthSessions,
  autoAssignLeads,
  getUserStatistics,
  getUnassignedUsers,
  bulkAssignLeads,
  updateGrowthSessionWithLead
} from '../../utils/growthSessionHelpers';
import {
  parseYourGrowthSessionResponse,
  assignLeadsToUsers,
  getSessionStatistics,
  formatSessionData
} from '../../utils/dynamoDbParser';

// Context for managing state
const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

const GrowthSessionManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Get user role and info from localStorage
  const [userRole, setUserRole] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [userBatch, setUserBatch] = useState(null);

  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [growthSessions, setGrowthSessions] = useState([]);
  const [pipPlans, setPipPlans] = useState([]);
  const [openGrowthSessionDialog, setOpenGrowthSessionDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [selectedUserForHistory, setSelectedUserForHistory] = useState(null);
  const [openPIPDialog, setOpenPIPDialog] = useState(false);
  const [openUserHierarchyDialog, setOpenUserHierarchyDialog] = useState(false);
  const [openUserHistoryDialog, setOpenUserHistoryDialog] = useState(false);
  const [editingGrowthSession, setEditingGrowthSession] = useState(null);
  const [viewingGrowthSession, setViewingGrowthSession] = useState(null);
  const [editingPIPPlan, setEditingPIPPlan] = useState(null);
  const [editingHierarchy, setEditingHierarchy] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedGrowthSessionId, setSelectedGrowthSessionId] = useState('');
  const [forceNewSession, setForceNewSession] = useState(false);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Filters for Users tab
  const [userFilters, setUserFilters] = useState({
    role: '',
    team: '',
    experienceLevel: ''
  });

  // Pagination state
  const [userPage, setUserPage] = useState(1);
  const usersPerPage = 10;


  // Sort state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });


  // Search for Growth Sessions tab
  const [growthSessionSearch, setGrowthSessionSearch] = useState('');


  const roles = ['Manager', 'Lead', 'User'];
  const experienceLevels = ['Junior', 'Mid-level', 'Senior', 'Expert'];
  const statusOptions = ['Draft', 'Submitted', 'Under Review', 'Completed'];

  // Initialize user role and data
  useEffect(() => {
    const initializeUserData = async () => {
      setLoading(true);

      try {
        const role = localStorage.getItem("userRole");
        const userEmail = localStorage.getItem("user");
        const userData = localStorage.getItem("userData");

        // Batch all initial state updates
        if (userData) {
          const parsedUserData = JSON.parse(userData);
          const batch = parsedUserData.user_batch;
          
          
          // Set all initial state in a single batch
          setUserRole(role);
          setCurrentUser(userEmail);
          setCurrentUserData(parsedUserData);
          setUserBatch(batch);
          
          // Set initial tab based on role
          if (role === "User") {
            setActiveTab('growth-sessions'); // Users can only see growth sessions tab
          } else {
            setActiveTab('users'); // Other roles start with users tab
          }

          // Load data after initial state is set
          await Promise.all([
            loadUsersByBatch(batch),
            loadGrowthSessions()
          ]);
        } else {
          // Set basic state even if no userData
          setUserRole(role);
          setCurrentUser(userEmail);
          console.log('No userData found in localStorage');
        }

      } catch (error) {
        console.error('Error loading user data:', error);
        showSnackbar('Error loading user data', 'error');
      } finally {
        setLoading(false);
      }
    };

    initializeUserData();
  }, []);

  // Role-based access control functions
  const canManageUsers = () => {
    return userRole === "Manager" || userRole === "Lead" || userRole === "Mentor";
  };

  const canCreateGrowthSession = () => {
    return userRole === "Manager" || userRole === "Lead" || userRole === "Mentor";
  };

  const canEditGrowthSession = (growthSession) => {
    if (userRole === "Manager" || userRole === "Mentor") return true;
    if (userRole === "Lead") {
      // Leads can only edit growth sessions for users assigned to them
      return growthSession.created_by === currentUser;
    }
    return false;
  };

  const canViewGrowthSession = (growthSession) => {
    if (userRole === "Manager") return true;
    
    if (userRole === "Mentor" || userRole === "Lead") {
      // Apply program code filtering for Mentors and Leads
      if (currentUserData && currentUserData.user_batch) {
        // Get the logged-in mentor/lead's program codes
        const mentorLeadProgramCodes = Array.isArray(currentUserData.user_batch) 
          ? currentUserData.user_batch 
          : currentUserData.user_batch.split(',').map(code => code.trim()).filter(Boolean);
        
        // Find the user this growth session belongs to
        const sessionUser = users.find(u => u.id === growthSession.user_id || u.email === growthSession.user_id);
        
        if (sessionUser) {
          // Get the session user's program codes (try both field names)
          const userProgramField = sessionUser.programCode || sessionUser.user_batch;
          const userProgramCodes = Array.isArray(userProgramField) 
            ? userProgramField 
            : (userProgramField || '').split(',').map(code => code.trim()).filter(Boolean);
          
          // Check if there's any overlap between mentor/lead and user program codes
          const hasCommonProgram = mentorLeadProgramCodes.some(mentorLeadCode => 
            userProgramCodes.includes(mentorLeadCode)
          );
          
          return hasCommonProgram;
        }
        return false;
      }
      return false;
    }
    
    if (userRole === "User") {
      // Users can only view their own growth sessions and PIP plans
      return growthSession.user_id === currentUser;
    }
    
    return false;
  };

  // User management functions

  const getUsersByRole = (role) => {
    return users.filter(user => user.role === role);
  };

  const getUserById = (userId) => {
    const user = users.find(u => u.id === userId || u.email === userId);
    return user ? user.email : null;
  };

  const getUserByEmail = (email) => {
    return users.find(user => user.email === email);
  };
  
  const getLeadById = (leadId) => {
    if (!leadId) return null;
    // Try to find by ID first, then by email, then by name
    return users.find(u => 
      u.id === leadId || 
      u.email === leadId ||
      u.name === leadId
    );
  };
  
  const getManagerById = (managerId) => {
    if (!managerId) return null;
    // Try to find by ID first, then by email, then by name
    return users.find(u => 
      u.id === managerId || 
      u.email === managerId ||
      u.name === managerId
    );
  };

  // DataGrid columns for growth sessions
  const growthSessionColumns = [
    {
      field: 'id',
      headerName: 'S. No',
      width: 70,
      minWidth: 70,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="700" color="primary.main" sx={{ fontSize: '0.8rem' }}>
          {params.api.getRowIndexRelativeToVisibleRows(params.row.id) + 1}
        </Typography>
      )
    },
    {
      field: 'user_id',
      headerName: 'Employee',
      width: 200,
      minWidth: 140,
      renderCell: (params) => {
        const user = users.find(u => u.id === params.row.user_id || u.email === params.row.user_id);
        const hasPIPPlan = params.row.pip_data && 
          (typeof params.row.pip_data === 'string' ? 
            JSON.parse(params.row.pip_data).final_decision : 
            params.row.pip_data.final_decision);
        
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
              sx={{ 
                width: 24, 
                height: 24, 
                fontSize: '0.75rem',
                bgcolor: 'primary.main',
                fontWeight: 700
              }}
            >
              {user?.name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.85rem', color: '#2c3e50' }}>
                {user?.name || 'Unknown User'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {user?.email || 'No email'}
                </Typography>
                {hasPIPPlan && (
                  <Chip
                    label="PIP"
                    size="small"
                    icon={<AssignmentIcon sx={{ fontSize: 10 }} />}
                    sx={{
                      height: 16,
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      backgroundColor: alpha('#ff9800', 0.1),
                      color: '#ff9800',
                      border: `1px solid ${alpha('#ff9800', 0.3)}`,
                      '& .MuiChip-icon': {
                        fontSize: 10,
                        color: '#ff9800'
                      },
                      '& .MuiChip-label': {
                        px: 0.5,
                        fontSize: '0.6rem'
                      }
                    }}
                  />
                )}
                {params.row._hasMultipleSessions && (
                  <Chip
                    label={`${params.row._totalSessions} sessions`}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      backgroundColor: alpha('#2196f3', 0.1),
                      color: '#2196f3',
                      border: `1px solid ${alpha('#2196f3', 0.3)}`,
                      '& .MuiChip-label': {
                        px: 0.5,
                        fontSize: '0.6rem'
                      }
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'date',
      headerName: 'Session Date',
      width: 130,
      minWidth: 100,
      hide: isMobile,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
          {formatDate(params.value)}
        </Typography>
      )
    },
    {
      field: 'review_period',
      headerName: 'Review Period',
      width: 200,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {formatDate(params.row.review_period_from)} - {formatDate(params.row.review_period_to)}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      minWidth: 120,
      renderCell: (params) => {
        const statusConfig = getStatusConfig(params.value);
        return (
          <Chip
            icon={statusConfig.icon}
            label={params.value?.charAt(0).toUpperCase() + params.value?.slice(1).replace('_', ' ') || 'Draft'}
            size="small"
            sx={{
              bgcolor: statusConfig.bgcolor,
              color: statusConfig.textColor,
              fontWeight: 700,
              fontSize: '0.7rem',
              border: `1px solid ${statusConfig.borderColor}`,
              transition: 'all 0.2s ease-in-out',
              '& .MuiChip-icon': {
                color: statusConfig.textColor,
                fontSize: 16
              },
              '& .MuiChip-label': {
                fontWeight: 700,
                fontSize: '0.7rem'
              },
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: `0 2px 8px ${alpha(statusConfig.textColor, 0.2)}`,
                bgcolor: alpha(statusConfig.textColor, 0.15)
              }
            }}
          />
        );
      }
    },
    {
      field: 'lead_id',
      headerName: 'Assigned Lead',
      width: 150,
      minWidth: 120,
      hide: isMobile,
      renderCell: (params) => {
        const lead = getLeadById(params.value);
        return (
          <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.8rem', color: '#34495e' }}>
            {lead?.name || 'Not assigned'}
          </Typography>
        );
      }
    },
    {
      field: 'manager_id',
      headerName: 'Assigned Manager',
      width: 160,
      minWidth: 130,
      hide: isMobile,
      renderCell: (params) => {
        const manager = getManagerById(params.value);
        return (
          <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.8rem', color: '#34495e' }}>
            {manager?.name || 'Not assigned'}
          </Typography>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
          {userRole === "User" ? (
            // For regular users, show only view button
            <Tooltip title="View Growth Conversation Details" arrow placement="top">
              <IconButton
                onClick={() => handleViewGrowthSession(params.row)}
                size="small"
                sx={{ 
                  padding: '6px',
                  backgroundColor: alpha('#17a2b8', 0.1),
                  color: '#17a2b8',
                  border: `1px solid ${alpha('#17a2b8', 0.3)}`,
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: alpha('#17a2b8', 0.2),
                    transform: 'scale(1.1)',
                    boxShadow: '0 2px 8px rgba(23, 162, 184, 0.3)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <VisibilityIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : (
            // For management roles, show all buttons
            <>
              {params.row.status !== 'pip_created' && (
                <Tooltip title="Create/Update Growth Conversation" arrow placement="top">
                  <IconButton
                    onClick={() => handleOpenGrowthForm(params.row.user_id, false, params.row)}
                    size="small"
                    sx={{ 
                      padding: '6px',
                      backgroundColor: alpha('#cc4049', 0.1),
                      color: '#cc4049',
                      border: `1px solid ${alpha('#cc4049', 0.3)}`,
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: alpha('#cc4049', 0.2),
                        transform: 'scale(1.1)',
                        boxShadow: '0 2px 8px rgba(204, 64, 73, 0.3)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <AddComment sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              {params.row.status !== 'lead_assigned' && params.row.status !== 'pip_created' && (
                <Tooltip title="Create/View PIP Plan" arrow placement="top">
                  <IconButton
                    onClick={() => handleCreatePIPPlan(params.row.id, params.row.user_id)}
                    size="small"
                    sx={{ 
                      padding: '6px',
                      backgroundColor: alpha('#ff9800', 0.1),
                      color: '#ff9800',
                      border: `1px solid ${alpha('#ff9800', 0.3)}`,
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: alpha('#ff9800', 0.2),
                        transform: 'scale(1.1)',
                        boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <AssignmentIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="View Timeline History" arrow placement="top">
                <IconButton
                  onClick={() => handleViewGrowthSessionHistory(params.row)}
                  size="small"
                  sx={{ 
                    padding: '6px',
                    backgroundColor: alpha('#6c757d', 0.1),
                    color: '#6c757d',
                    border: `1px solid ${alpha('#6c757d', 0.3)}`,
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: alpha('#6c757d', 0.2),
                      transform: 'scale(1.1)',
                      boxShadow: '0 2px 8px rgba(108, 117, 125, 0.3)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <HistoryIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      )
    }
  ];

  // Toolbar components removed - using CommonTable built-in search

  // Load Growth Sessions from the database
  const loadGrowthSessions = async () => {
    try {
      const response = await getAllGrowthSessionsAPI();
      console.log('Raw getAllGrowthSessions response:', response);
      
      if (response.data && response.data.statusCode === 200) {
        const result = JSON.parse(response.data.body);
        console.log('Parsed response body:', result);
        
        if (result.growthSessions) {
          // Parse your specific DynamoDB format
          const parsedSessions = parseYourGrowthSessionResponse(result);
          console.log('Parsed sessions:', parsedSessions);
          
          // Auto-assign leads to users who don't have them
          const sessionsWithLeads = await assignLeadsToUsers(parsedSessions, users, updateGrowthSessionAPI);
          
          // Show statistics
          const stats = getSessionStatistics(sessionsWithLeads, users);
          console.log('Session statistics:', stats);
          
          if (stats.usersWithoutLeads > 0) {
            showSnackbar(`${stats.usersWithoutLeads} users still need lead assignment`, 'info');
          }
          
          setGrowthSessions(sessionsWithLeads);
          
          // Load PIP plans after growth sessions are loaded
          loadPIPPlansFromSessions(sessionsWithLeads);
        }
      }
    } catch (error) {
      console.error('Error loading growth sessions:', error);
      showSnackbar('Error loading growth Conversations', 'error');
    }
  };

  // Load PIP Plans from growth sessions (pip_data field) using provided sessions
  const loadPIPPlansFromSessions = (sessions) => {
    try {
      // PIP plans are now embedded in growth sessions as pip_data
      const pipPlansFromGrowthSessions = sessions.map(session => {
        try {
          const sessionData = typeof session.session_data === 'string' 
            ? JSON.parse(session.session_data) 
            : session.session_data;
          
          const pipData = typeof session.pip_data === 'string' 
            ? JSON.parse(session.pip_data) 
            : session.pip_data;

          if (pipData && Object.keys(pipData).length > 0 && pipData.final_decision) {
            return {
              id: session.id,
              user_id: session.user_id,
              growth_session_id: session.id,
              final_decision: pipData.final_decision,
              location: pipData.location,
              release_date: pipData.release_date,
              pip_start_date: pipData.pip_start_date,
              pip_end_date: pipData.pip_end_date,
              attached_files: pipData.attached_files || [],
              dynamic_fields: pipData.dynamic_fields || {},
              created_at: session.created_at,
              updated_at: session.updated_at,
              created_by: session.created_by
            };
          }
          return null;
        } catch (error) {
          console.error('Error parsing growth session data:', error);
          return null;
        }
      }).filter(pipPlan => pipPlan !== null);

      setPipPlans(pipPlansFromGrowthSessions);
    } catch (error) {
      console.error('Error loading PIP plans from growth sessions:', error);
      showSnackbar('Error loading PIP plans', 'error');
    }
  };

  // Legacy loadPIPPlans function that uses current state
  const loadPIPPlans = async () => {
    loadPIPPlansFromSessions(growthSessions);
  };

  // Growth Session management functions
  const addGrowthSession = async (growthSession) => {
    try {
      const newGrowthSession = { 
        ...growthSession, 
        id: Date.now().toString(), 
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: currentUser
      };
      
      const response = await createGrowthSessionAPI(newGrowthSession);
      if (response.data && response.data.statusCode === 200) {
        setGrowthSessions(prev => [...prev, newGrowthSession]);
        showSnackbar('Growth Conversation created successfully', 'success');
      } else {
        showSnackbar('Failed to create growth Conversation', 'error');
      }
    } catch (error) {
      console.error('Error creating growth session:', error);
      showSnackbar('Error creating growth Conversation', 'error');
    }
  };

  const updateGrowthSessionLocal = async (growthSessionId, updatedGrowthSession) => {
    try {
      const updateData = {
        ...updatedGrowthSession,
        id: growthSessionId,
        updated_at: new Date().toISOString()
      };
      
      const response = await updateGrowthSessionAPI(updateData);
      if (response.data && response.data.statusCode === 200) {
        setGrowthSessions(prev => prev.map(session =>
          session.id === growthSessionId ? updateData : session
        ));
        showSnackbar('Growth Conversation updated successfully', 'success');
      } else {
        showSnackbar('Failed to update growth Conversation', 'error');
      }
    } catch (error) {
      console.error('Error updating growth Conversation:', error);
      showSnackbar('Error updating growth Conversation', 'error');
    }
  };

  const deleteGrowthSessionLocal = async (growthSessionId) => {
    try {
      const response = await deleteGrowthSessionAPI({ id: growthSessionId });
      if (response.data && response.data.statusCode === 200) {
        setGrowthSessions(prev => prev.filter(session => session.id !== growthSessionId));
        showSnackbar('Growth Conversation deleted successfully', 'success');
      } else {
        showSnackbar('Failed to delete growth Conversation', 'error');
      }
    } catch (error) {
      console.error('Error deleting growth Conversation:', error);
      showSnackbar('Error deleting growth Conversation', 'error');
    }
  };

  // PIP Plan management functions - now uses growth session pip_data
  const addPIPPlanLocal = async (pipPlan) => {
    try {
      // Find the growth session associated with this PIP plan
      const growthSessionId = pipPlan.growth_session_id;
      const growthSession = growthSessions.find(session => session.id === growthSessionId);
      
      if (!growthSession) {
        showSnackbar('Growth Conversation not found for PIP plan', 'error');
        return;
      }

      // Create the pip_data object
      const pipData = {
        final_decision: pipPlan.final_decision,
        location: pipPlan.location,
        release_date: pipPlan.release_date,
        pip_start_date: pipPlan.pip_start_date,
        pip_end_date: pipPlan.pip_end_date,
        attached_files: pipPlan.attached_files || [],
        dynamic_fields: pipPlan.dynamic_fields || {}
      };

      // Update the growth session with pip_data
      const updateData = {
        id: growthSessionId,
        user_id: growthSession.user_id,
        pip_data: pipData,
        updated_at: new Date().toISOString()
      };
      
      const response = await updateGrowthSessionAPI(updateData);
      if (response.data && response.data.statusCode === 200) {
        // Update local growth session state
        setGrowthSessions(prev => prev.map(session =>
          session.id === growthSessionId 
            ? { ...session, pip_data: JSON.stringify(pipData), updated_at: updateData.updated_at }
            : session
        ));
        showSnackbar('PIP plan created successfully', 'success');
        // Refresh PIP plans from updated growth sessions
        await loadPIPPlans();
      } else {
        showSnackbar('Failed to create PIP plan', 'error');
      }
    } catch (error) {
      console.error('Error creating PIP plan:', error);
      showSnackbar('Error creating PIP plan', 'error');
    }
  };

  const updatePIPPlanLocal = async (pipPlanId, updatedPIPPlan) => {
    try {
      // Find the growth session (pipPlanId is the same as growth session id)
      const growthSessionId = pipPlanId;
      const growthSession = growthSessions.find(session => session.id === growthSessionId);
      
      if (!growthSession) {
        showSnackbar('Growth Conversation not found for PIP plan', 'error');
        return;
      }

      // Create the updated pip_data object
      const pipData = {
        final_decision: updatedPIPPlan.final_decision,
        location: updatedPIPPlan.location,
        release_date: updatedPIPPlan.release_date,
        pip_start_date: updatedPIPPlan.pip_start_date,
        pip_end_date: updatedPIPPlan.pip_end_date,
        attached_files: updatedPIPPlan.attached_files || [],
        dynamic_fields: updatedPIPPlan.dynamic_fields || {}
      };

      // Update the growth session with new pip_data
      const updateData = {
        id: growthSessionId,
        user_id: growthSession.user_id,
        pip_data: pipData,
        updated_at: new Date().toISOString()
      };
      
      const response = await updateGrowthSessionAPI(updateData);
      if (response.data && response.data.statusCode === 200) {
        // Update local growth session state
        setGrowthSessions(prev => prev.map(session =>
          session.id === growthSessionId 
            ? { ...session, pip_data: JSON.stringify(pipData), updated_at: updateData.updated_at }
            : session
        ));
        showSnackbar('PIP plan updated successfully', 'success');
        // Refresh PIP plans from updated growth sessions
        await loadPIPPlans();
      } else {
        showSnackbar('Failed to update PIP plan', 'error');
      }
    } catch (error) {
      console.error('Error updating PIP plan:', error);
      showSnackbar('Error updating PIP plan', 'error');
    }
  };

  const deletePIPPlanLocal = async (pipPlanId) => {
    try {
      // Find the growth session (pipPlanId is the same as growth session id)
      const growthSessionId = pipPlanId;
      const growthSession = growthSessions.find(session => session.id === growthSessionId);
      
      if (!growthSession) {
        showSnackbar('Growth Conversation not found for PIP plan', 'error');
        return;
      }

      // Clear the pip_data by setting it to empty object
      const emptyPipData = {
        final_decision: "",
        location: "",
        release_date: "",
        pip_start_date: "",
        pip_end_date: "",
        attached_files: [],
        dynamic_fields: {}
      };

      // Update the growth session to clear pip_data
      const updateData = {
        id: growthSessionId,
        user_id: growthSession.user_id,
        pip_data: emptyPipData,
        updated_at: new Date().toISOString()
      };
      
      const response = await updateGrowthSessionAPI(updateData);
      if (response.data && response.data.statusCode === 200) {
        // Update local growth session state
        setGrowthSessions(prev => prev.map(session =>
          session.id === growthSessionId 
            ? { ...session, pip_data: JSON.stringify(emptyPipData), updated_at: updateData.updated_at }
            : session
        ));
        showSnackbar('PIP plan deleted successfully', 'success');
        // Refresh PIP plans from updated growth sessions
        await loadPIPPlans();
      } else {
        showSnackbar('Failed to delete PIP plan', 'error');
      }
    } catch (error) {
      console.error('Error deleting PIP plan:', error);
      showSnackbar('Error deleting PIP plan', 'error');
    }
  };

  const getPIPPlansByUser = (userId) => {
    return pipPlans.filter(pipPlan => pipPlan.user_id === userId);
  };

  const getPIPPlansByGrowthSession = (growthSessionId) => {
    return pipPlans.filter(pipPlan => pipPlan.growth_session_id === growthSessionId);
  };

  // Load users from the database - load all users for lead lookup, filter display separately
  const loadUsersByBatch = async (batchCode) => {
    try {
      const allUsers = await getUsers();
      console.log(allUsers);
      if (allUsers && allUsers.length > 0) {
        // For user roles, we need all users available for lead lookup
        // but we'll filter the display through filteredUsers
        setUsers(allUsers);
      }
    } catch (error) {
      console.error('Error loading users by batch:', error);
      showSnackbar('Error loading users', 'error');
    }
  };

  // Refresh users data
  const refreshUsers = async () => {
    if (userBatch) {
      await loadUsersByBatch(userBatch);
    }
  };

  // Snackbar function
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Event handlers
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  // Define tabs configuration
  const getTabs = () => {
    const baseTabs = [];

    if (canManageUsers()) {
      baseTabs.push({
        id: 'users',
        label: 'Users',
        title: 'User Management',
        description: 'Manage users and create growth conversation sessions to foster professional development and skill improvement.'
      });
    }

    baseTabs.push({
      id: 'growth-sessions',
      label: 'Growth Sessions',
      title: 'Growth Conversations',
      description: userRole === "User"
        ? 'View your growth conversation sessions and feedback to track your professional development progress.'
        : 'Manage and track all growth conversation sessions, provide feedback, and monitor team member progress.'
    });

    return baseTabs;
  };



  /**
   * Common handler for opening growth form - used by both users and feedback tabs
   * 
   * @param {string} userId - The user ID for whom to open the growth form
   * @param {boolean} forceNew - Whether to force create a new session (ignoring existing ones)
   * @param {object} specificSession - If provided, edit this specific session (from feedback tab)
   * 
   * Usage:
   * - From Users tab: handleOpenGrowthForm(userId, forceNew) - automatically finds existing sessions
   * - From Feedback tab: handleOpenGrowthForm(userId, false, session) - edit specific session
   * - Force new session: handleOpenGrowthForm(userId, true) - create new regardless of existing
   */
  const handleOpenGrowthForm = (userId = '', forceNew = false, specificSession = null) => {
    setSelectedUserId(userId);
    setForceNewSession(forceNew);
    
    if (specificSession) {
      // Direct editing of a specific session (from feedback tab)
      setEditingGrowthSession(specificSession);
      setOpenGrowthSessionDialog(true);
      return;
    }
    
    if (forceNew) {
      // Force a completely new session (e.g., after PIP completion)
      setEditingGrowthSession(null);
      setOpenGrowthSessionDialog(true);
      return;
    }
    
    // Look for existing ACTIVE/INCOMPLETE lead assignment (not completed ones)
    // This allows multiple growth sessions per user across different review periods
    console.log(growthSessions, 'userId:', userId);
    
    const activeStates = ['draft', 'assignment', 'lead_assigned', 'in_progress', 'scheduled', 'pip_created'];
    const existingActiveAssignment = growthSessions.find(session => 
      session.user_id === userId && activeStates.includes(session.status)
    );
    
    console.log('Existing active assignment:', existingActiveAssignment);
    
    if (existingActiveAssignment) {
      // Edit the existing active assignment to add feedback
      setEditingGrowthSession(existingActiveAssignment);
    } else {
      // No existing active assignment found, create new growth session
      // This allows creating new sessions for users with completed previous sessions
      setEditingGrowthSession(null);
    }
    
    setOpenGrowthSessionDialog(true);
  };

  const handleCreateGrowthSession = (userId = '', forceNew = false) => {
    // Wrapper for backward compatibility - use the common handler
    handleOpenGrowthForm(userId, forceNew);
  };


  const handleViewGrowthSession = (growthSession) => {
    setViewingGrowthSession(growthSession);
    setOpenViewDialog(true);
  };

  const handleViewGrowthSessionHistory = (growthSession) => {
    // Set the user from the growth session
    const user = users.find(u => u.id === growthSession.user_id || u.email === growthSession.user_id);
    setSelectedUserForHistory(user || { user_id: growthSession.user_id });
    setOpenHistoryDialog(true);
  };


  const handleCloseGrowthSessionDialog = () => {
    setOpenGrowthSessionDialog(false);
    setEditingGrowthSession(null);
    setSelectedUserId('');
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewingGrowthSession(null);
  };


  const handleCreateUserHierarchy = (userId = '') => {
    setSelectedUserId(userId);
    setEditingHierarchy(null);
    setOpenUserHierarchyDialog(true);
  };

  const handleEditUserHierarchy = (hierarchy) => {
    setEditingHierarchy(hierarchy);
    setOpenUserHierarchyDialog(true);
    handleCloseMenu();
  };

  const handleCloseUserHierarchyDialog = () => {
    setOpenUserHierarchyDialog(false);
    setEditingHierarchy(null);
  };

  const handleCreatePIPPlan = (growthSessionId = '', userId = '') => {
    setSelectedGrowthSessionId(growthSessionId);
    setSelectedUserId(userId);
    setEditingPIPPlan(null);
    setOpenPIPDialog(true);
  };

  const handleEditPIPPlan = (pipPlan) => {
    setEditingPIPPlan(pipPlan);
    setSelectedGrowthSessionId(pipPlan.growth_session_id || '');
    setSelectedUserId(pipPlan.user_id || '');
    setOpenPIPDialog(true);
    handleCloseMenu();
  };

  const handleClosePIPDialog = () => {
    setOpenPIPDialog(false);
    setEditingPIPPlan(null);
    setSelectedGrowthSessionId('');
    setSelectedUserId('');
  };

  const handleViewUserHistory = (userId, userName) => {
    // Users can only view their own history
    if (userRole === "User" && userId !== currentUser) {
      showSnackbar('You can only view your own history', 'error');
      return;
    }
    setSelectedUserForHistory({ id: userId, name: userName });
    setOpenUserHistoryDialog(true);
  };

  const handleCloseUserHistoryDialog = () => {
    setOpenUserHistoryDialog(false);
    setSelectedUserForHistory(null);
  };

  const handlePIPSuccess = async (action) => {
    showSnackbar(`PIP ${action} successfully`, 'success');
    
    // Refresh growth sessions from server to ensure latest status is reflected
    await loadGrowthSessions();
    
    // Close the dialog
    handleClosePIPDialog();
  };

  const handleGrowthSessionSuccess = async (action) => {
    showSnackbar(`Growth Conversation ${action} successfully`, 'success');
    
    // Refresh growth sessions from server to ensure latest status is reflected
    await loadGrowthSessions();
    
    // Close the dialog
    handleCloseGrowthSessionDialog();
  };

  const handleHierarchySuccess = async (action, newGrowthSession) => {
    showSnackbar(`Team Member assignment ${action} successfully`, 'success');
    
    // Refresh growth sessions from server to ensure latest data
    await loadGrowthSessions();
    
    // Close the dialog
    handleCloseUserHierarchyDialog();
  };

  const handleMenuClick = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };


  const handleClearFilters = (type) => {
    if (type === 'users') {
      setUserFilters({
        role: '',
        team: '',
        experienceLevel: ''
      });
    } else {
      setGrowthSessionSearch('');
    }
  };

  // Filter functions with role-based access
  const filteredUsers = useMemo(() => users.filter(user => {
    // For Mentors and Leads, filter users by program codes
    if (userRole === "Mentor" || userRole === "Lead") {
      if (currentUserData && currentUserData.user_batch) {
        // Get the logged-in mentor/lead's program codes
        const mentorLeadProgramCodes = Array.isArray(currentUserData.user_batch) 
          ? currentUserData.user_batch 
          : currentUserData.user_batch.split(',').map(code => code.trim()).filter(Boolean);
        
        // Get the user's program codes (try both field names)
        const userProgramField = user.programCode || user.user_batch;
        const userProgramCodes = Array.isArray(userProgramField) 
          ? userProgramField 
          : (userProgramField || '').split(',').map(code => code.trim()).filter(Boolean);
        
        // Check if there's any overlap between mentor/lead and user program codes
        const hasCommonProgram = mentorLeadProgramCodes.some(mentorLeadCode => 
          userProgramCodes.includes(mentorLeadCode)
        );
        
        if (!hasCommonProgram) {
          return false;
        }
      }
    }
    
    // Role-based filtering
    if (userRole === "Lead") {
      // Leads can only see users assigned to them and other users in their hierarchy
      const isAssignedToLead = user.assignedLead === currentUser;
      const currentUserFromUsers = users.find(u => u.id === currentUser || u.email === currentUser);
      const isInSameTeam = user.teamName === currentUserFromUsers?.teamName;
      if (!isAssignedToLead && !isInSameTeam && user.role !== "Manager" && user.role !== "Mentor") {
        return false;
      }
    } else if (userRole === "User") {
      // Users can only see their own profile and their lead/manager/mentor from same batch
      if (user.id !== currentUser && user.role !== "Lead" && user.role !== "Manager" && user.role !== "Mentor") {
        return false;
      }
    }
    // Managers can see all users from same batch

    return true;
  }), [users, userRole, currentUser, currentUserData]);

  // Sort users based on sortConfig
  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return filteredUsers;

    return [...filteredUsers].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle different data types
      if (sortConfig.key === 'name') {
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
      } else if (sortConfig.key === 'email') {
        aValue = a.email?.toLowerCase() || '';
        bValue = b.email?.toLowerCase() || '';
      } else if (sortConfig.key === 'role') {
        aValue = a.role?.toLowerCase() || '';
        bValue = b.role?.toLowerCase() || '';
      } else if (sortConfig.key === 'teamName') {
        aValue = a.teamName?.toLowerCase() || '';
        bValue = b.teamName?.toLowerCase() || '';
      } else if (sortConfig.key === 'experienceLevel') {
        // Custom order for experience levels
        const experienceOrder = { 'junior': 1, 'mid-level': 2, 'senior': 3, 'expert': 4 };
        aValue = experienceOrder[a.experienceLevel?.toLowerCase()] || 0;
        bValue = experienceOrder[b.experienceLevel?.toLowerCase()] || 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  // Pagination logic for users
  const totalPages = Math.ceil(sortedUsers.length / usersPerPage);
  const startIndex = (userPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const paginatedUsers = sortedUsers.slice(startIndex, endIndex);

  const handleUserPageChange = (event, page) => {
    setUserPage(page);
  };

  // Sort function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setUserPage(1); // Reset to first page when sorting
  };

  // Get sort icon for table headers
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    }
    return sortConfig.direction === 'asc' ? 
      <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : 
      <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  // Reset pagination when filters change
  useEffect(() => {
    setUserPage(1);
  }, [userFilters]);

  const filteredGrowthSessions = useMemo(() => {
    // First filter by role-based access and search
    const accessFilteredSessions = growthSessions.filter(growthSession => {
      // Role-based access control for growth sessions
      if (!canViewGrowthSession(growthSession)) {
        return false;
      }
      
      
      // Search functionality
      if (growthSessionSearch) {
        const user = users.find(u => u.id === growthSession.user_id || u.email === growthSession.user_id);
        const lead = getLeadById(growthSession.lead_id);
        const searchLower = growthSessionSearch.toLowerCase();
        
        const matchesSearch = 
          (user?.name?.toLowerCase().includes(searchLower)) ||
          (user?.email?.toLowerCase().includes(searchLower)) ||
          (lead?.name?.toLowerCase().includes(searchLower)) ||
          (growthSession.status?.toLowerCase().includes(searchLower)) ||
          (growthSession.session_data?.key_strengths?.toLowerCase().includes(searchLower)) ||
          (growthSession.session_data?.areas_for_improvement?.toLowerCase().includes(searchLower));
        
        if (!matchesSearch) return false;
      }

      return true;
    });

    // For User role, show all sessions individually. For other roles, deduplicate.
    if (userRole === "User") {
      // Users see all their own sessions listed individually
      return accessFilteredSessions.sort((a, b) => {
        // Sort by most recent first
        const aDate = new Date(a.created_at || a.updated_at || 0);
        const bDate = new Date(b.created_at || b.updated_at || 0);
        return bDate - aDate;
      });
    }
    
    // For management roles, deduplicate users - show only one session per user with priority system
    const userSessionMap = new Map();
    
    // Group sessions by user_id
    accessFilteredSessions.forEach(session => {
      const userId = session.user_id;
      if (!userId) return;
      
      if (!userSessionMap.has(userId)) {
        userSessionMap.set(userId, []);
      }
      userSessionMap.get(userId).push(session);
    });
    
    // Select the best session for each user
    const deduplicatedSessions = [];
    userSessionMap.forEach((userSessions) => {
      if (userSessions.length === 1) {
        deduplicatedSessions.push(userSessions[0]);
        return;
      }
      
      // Priority system for selecting which session to show:
      // 1. In progress sessions (in_progress, assignment, lead_assigned)
      // 2. Most recent session (by created_at)
      // 3. Sessions with more complete data
      const priorityOrder = ['in_progress', 'assignment', 'lead_assigned', 'growth_session', 'pip_created', 'scheduled', 'draft'];
      
      const bestSession = userSessions.sort((a, b) => {
        // Priority by status
        const aStatusIndex = priorityOrder.indexOf(a.status) !== -1 ? priorityOrder.indexOf(a.status) : 999;
        const bStatusIndex = priorityOrder.indexOf(b.status) !== -1 ? priorityOrder.indexOf(b.status) : 999;
        
        if (aStatusIndex !== bStatusIndex) {
          return aStatusIndex - bStatusIndex; // Lower index = higher priority
        }
        
        // If same status priority, prefer more recent
        const aDate = new Date(a.created_at || a.updated_at || 0);
        const bDate = new Date(b.created_at || b.updated_at || 0);
        
        if (aDate !== bDate) {
          return bDate - aDate; // Newer first
        }
        
        // If same date, prefer sessions with more complete data
        const aHasData = !!(a.session_data && Object.keys(a.session_data).length > 0);
        const bHasData = !!(b.session_data && Object.keys(b.session_data).length > 0);
        
        if (aHasData !== bHasData) {
          return bHasData - aHasData; // Sessions with data first
        }
        
        return 0;
      })[0];
      
      deduplicatedSessions.push({
        ...bestSession,
        // Add metadata to indicate multiple sessions exist
        _hasMultipleSessions: userSessions.length > 1,
        _totalSessions: userSessions.length
      });
    });
    
    return deduplicatedSessions;
  }, [growthSessions, growthSessionSearch, users, currentUser, userRole, currentUserData]);

  // Helper functions
  const getAssignedPersonName = (user) => {
    if (user.role === 'User') {
      // Check for lead from user profile first
      if (user.assignedLead) {
        const lead = getUserByEmail(user.assignedLead) || users.find(u => u.id === user.assignedLead);
        if (lead) return lead.name; // Reports to lead
      } else {
        // Check for lead from growth sessions if not in user profile
        const userGrowthSession = growthSessions.find(gs => gs.user_id === user.email || gs.user_id === user.id);
        if (userGrowthSession && userGrowthSession.lead_id) {
          const lead = getLeadById(userGrowthSession.lead_id);
          if (lead) return lead.name; // Reports to lead
        }
      }
    }

    if (user.role === 'Lead' && user.assignedManager) {
      const manager = getUserByEmail(user.assignedManager) || users.find(u => u.id === user.assignedManager);
      if (manager) return manager.name; // Lead reports to manager
    }

    return '-'; // No reporting relationship found
  };

  // Check if user already has a growth session with 'lead_assigned' status
  const hasLeadAssignedStatus = (user) => {
    return growthSessions.some(gs => 
      (gs.user_id === user.email || gs.user_id === user.id) && 
      gs.status === 'lead_assigned'
    );
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Manager': return 'primary';
      case 'Lead': return 'secondary';
      case 'Mentor': return 'info';
      default: return 'default';
    }
  };

  // Helper functions for the new table style
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (error) {
      return 'N/A';
    }
  };


  const getStatusConfig = (status) => {
    switch (status) {
      case 'pip_created':
        return {
          icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
          bgcolor: alpha('#4caf50', 0.1),
          textColor: '#2e7d32',
          borderColor: alpha('#4caf50', 0.3)
        };
      case 'assignment':
      case 'lead_assigned':
        return {
          icon: <AssignmentIcon sx={{ fontSize: 16 }} />,
          bgcolor: alpha('#2196f3', 0.1),
          textColor: '#1565c0',
          borderColor: alpha('#2196f3', 0.3)
        };
      case 'in_progress':
      case 'growth_session':
        return {
          icon: <WarningIcon sx={{ fontSize: 16 }} />,
          bgcolor: alpha('#ff9800', 0.1),
          textColor: '#e65100',
          borderColor: alpha('#ff9800', 0.3)
        };
      case 'draft':
        return {
          icon: <InfoIcon sx={{ fontSize: 16 }} />,
          bgcolor: alpha('#9e9e9e', 0.1),
          textColor: '#424242',
          borderColor: alpha('#9e9e9e', 0.3)
        };
      case 'scheduled':
        return {
          icon: <CalendarTodayIcon sx={{ fontSize: 16 }} />,
          bgcolor: alpha('#9c27b0', 0.1),
          textColor: '#6a1b9a',
          borderColor: alpha('#9c27b0', 0.3)
        };
      case 'pip_created':
        return {
          icon: <AssignmentIcon sx={{ fontSize: 16 }} />,
          bgcolor: alpha('#ff9800', 0.1),
          textColor: '#f57c00',
          borderColor: alpha('#ff9800', 0.3)
        };
      default:
        return {
          icon: <InfoIcon sx={{ fontSize: 16 }} />,
          bgcolor: alpha('#607d8b', 0.1),
          textColor: '#455a64',
          borderColor: alpha('#607d8b', 0.3)
        };
    }
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'pip_created': return 'success';
      case 'assignment': return 'info';
      case 'in_progress': return 'warning';
      case 'draft': return 'default';
      case 'scheduled': return 'primary';
      case 'Completed': return 'success';
      case 'Under Review': return 'warning';
      case 'Submitted': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed': return <CheckCircleIcon fontSize="small" />;
      case 'Under Review': return <WarningIcon fontSize="small" />;
      case 'Submitted': return <InfoIcon fontSize="small" />;
      default: return <EditIcon fontSize="small" />;
    }
  };


  const contextValue = {
    users,
    growthSessions,
    pipPlans,
    currentUserData,
    userBatch,
    getLeadById,
    getManagerById,
    getUsersByRole,
    getUserById,
    getUserByEmail,
    addGrowthSession,
    updateGrowthSession: updateGrowthSessionLocal,
    deleteGrowthSession: deleteGrowthSessionLocal,
    addPIPPlan: addPIPPlanLocal,
    updatePIPPlan: updatePIPPlanLocal,
    deletePIPPlan: deletePIPPlanLocal,
    getPIPPlansByUser,
    getPIPPlansByGrowthSession,
    loadGrowthSessions,
    loadPIPPlans,
    refreshUsers
  };

  // Statistics Cards - KPI Management Dashboard style
  const StatCard = ({ title, value, icon, subtitle, trend, trendColor = '#6B7280' }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        backgroundColor: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: '#3B82F6'
        }
      }}
    >
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{
          fontSize: '14px',
          color: '#6B7280',
          fontFamily: 'Poppins',
          fontWeight: 400
        }}>
          {title}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h4" sx={{
          fontWeight: 700,
          fontSize: { xs: '24px', sm: '28px', md: '32px' },
          color: '#111827',
          fontFamily: 'Poppins'
        }}>
          {value}
        </Typography>
        <Box sx={{
          p: 1,
          borderRadius: '8px',
          backgroundColor: '#F3F4F6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {React.cloneElement(icon, {
            sx: {
              fontSize: 20,
              color: '#6B7280'
            }
          })}
        </Box>
      </Box>

      {subtitle && (
        <Typography variant="body2" sx={{
          fontSize: '12px',
          color: '#9CA3AF',
          fontFamily: 'Poppins',
          mb: 1
        }}>
          {subtitle}
        </Typography>
      )}

      {trend && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: '12px',
          color: trendColor,
          fontFamily: 'Poppins'
        }}>
          {trend}
        </Box>
      )}
    </Paper>
  );

  // Tab Panel Component removed - using TabsLayout instead

  // Empty State Component
  const EmptyState = ({ message, action }) => (
    <Box
      sx={{
        py: 8,
        px: 2,
        textAlign: 'center',
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        borderRadius: 2
      }}
    >
      <Avatar
        sx={{
          width: 80,
          height: 80,
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          mx: 'auto',
          mb: 1
        }}
      >
        <InfoIcon sx={{ fontSize: 40, color: 'primary.main' }} />
      </Avatar>
      <Typography variant="h6" gutterBottom color="text.secondary">
        {message}
      </Typography>
      {action && (
        <Box sx={{ mt: 1 }}>
          {action}
        </Box>
      )}
    </Box>
  );

  // DataGrid columns for users
  const userColumns = [
    {
      field: 'id',
      headerName: 'S. No',
      width: 70,
      minWidth: 70,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="700" color="primary.main" sx={{ fontSize: '0.8rem' }}>
          {params.api.getRowIndexRelativeToVisibleRows(params.row.id) + 1}
        </Typography>
      )
    },
    {
      field: 'name',
      headerName: 'Employee Name',
      width: 200,
      minWidth: 140,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar
            sx={{ 
              width: 24, 
              height: 24, 
              fontSize: '0.75rem',
              bgcolor: 'primary.main',
              fontWeight: 700
            }}
          >
            {params.row.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.85rem', color: '#2c3e50' }}>
            {params.row.name}
          </Typography>
        </Box>
      )
    },
    {
      field: 'email',
      headerName: 'Email Address',
      width: 250,
      minWidth: 240,
      flex: 1,
      hide: isMobile,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', fontFamily: 'monospace' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 150,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 'User' ? 'Team Member' : params.value}
          color={getRoleColor(params.value)}
          size="small"
          sx={{ 
            fontWeight: 700,
            fontSize: '0.7rem',
            height: 24,
            '& .MuiChip-label': {
              px: 1.5,
              fontSize: '0.7rem',
              fontWeight: 700
            }
          }}
        />
      )
    },
    {
      field: 'experience_level',
      headerName: 'Experience',
      width: 110,
      minWidth: 100,
      hide: isTablet,
      renderCell: (params) => (
        <Chip
          label={`${params.value || 0} yrs`}
          size="small"
          variant="outlined"
          sx={{
            fontSize: '0.7rem',
            height: 22,
            fontWeight: 600,
            '& .MuiChip-label': {
              px: 1,
              fontSize: '0.7rem',
              fontWeight: 600
            }
          }}
        />
      )
    },
    {
      field: 'assignedTo',
      headerName: 'Reports To',
      width: 200,
      minWidth: 150,
      hide: isMobile,
      valueGetter: (params) => getAssignedPersonName(params.row),
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.8rem', color: '#34495e' }}>
          {getAssignedPersonName(params.row) || 'Not Assigned'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => {
        // Look for ACTIVE growth sessions only (not completed ones) - get the most recent one
        const activeStates = ['draft', 'assignment', 'lead_assigned', 'in_progress', 'scheduled', 'growth_session', 'pip_created'];
        const userActiveSessions = growthSessions.filter(gs => 
          (gs.user_id === params.row.email || gs.user_id === params.row.id) && 
          activeStates.includes(gs.status)
        );
        
        // Get the most recent active session by sorting by created_at
        const userActiveGrowthSession = userActiveSessions.length > 0 
          ? userActiveSessions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
          : null;
          
        // Debug logging for troubleshooting
        if (params.row.email && userActiveSessions.length > 0) {
          console.log(`User ${params.row.email} active sessions:`, userActiveSessions.map(s => ({ id: s.id, status: s.status, created_at: s.created_at })));
          console.log(`Selected active session:`, userActiveGrowthSession ? { id: userActiveGrowthSession.id, status: userActiveGrowthSession.status } : 'None');
        }
        
        const hasFeedbackAdded = userActiveGrowthSession && userActiveGrowthSession.status === 'growth_session';
        const hasLeadAssignedOnly = userActiveGrowthSession && userActiveGrowthSession.status === 'lead_assigned';
        
        return (
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
            {params.row.role === 'User' && (
              <>
                {userRole === "User" ? (
                  // Users can only view - show view button if growth session exists
                  hasFeedbackAdded && (
                    <Tooltip title="View Growth Conversation Details" arrow placement="top">
                      <IconButton
                        onClick={() => handleViewGrowthSession(userActiveGrowthSession)}
                        size="small"
                        sx={{ 
                          padding: '6px',
                          backgroundColor: alpha('#17a2b8', 0.1),
                          color: '#17a2b8',
                          border: `1px solid ${alpha('#17a2b8', 0.3)}`,
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: alpha('#17a2b8', 0.2),
                            transform: 'scale(1.1)',
                            boxShadow: '0 2px 8px rgba(23, 162, 184, 0.3)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <VisibilityIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )
                ) : canCreateGrowthSession() ? (
                  // Managers/Leads/Mentors can create and manage
                  !userActiveGrowthSession || (userActiveGrowthSession && userActiveGrowthSession.status === 'pip_created') ? (
                    // Show assign user button when: 1) no growth session exists at all, OR 2) PIP has been created (ready for next review)
                    <Tooltip title="Assign User to Manager" arrow placement="top">
                      <IconButton
                        onClick={() => handleCreateUserHierarchy(params.row.id)}
                        size="small"
                        sx={{ 
                          padding: '6px',
                          backgroundColor: alpha('#0a0068', 0.1),
                          color: '#0a0068',
                          border: `1px solid ${alpha('#0a0068', 0.3)}`,
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: alpha('#0a0068', 0.2),
                            transform: 'scale(1.1)',
                            boxShadow: '0 2px 8px rgba(10, 0, 104, 0.3)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  ) : hasFeedbackAdded ? (
                    // Show growth, view and PIP icons when growth session has growth_session (status = 'growth_session')
                    <>
                      <Tooltip title="Edit Growth Conversation" arrow placement="top">
                        <IconButton
                          onClick={() => handleOpenGrowthForm(params.row.email, false, userActiveGrowthSession)}
                          size="small"
                          sx={{ 
                            padding: '6px',
                            backgroundColor: alpha('#cc4049', 0.1),
                            color: '#cc4049',
                            border: `1px solid ${alpha('#cc4049', 0.3)}`,
                            borderRadius: '8px',
                            '&:hover': {
                              backgroundColor: alpha('#cc4049', 0.2),
                              transform: 'scale(1.1)',
                              boxShadow: '0 2px 8px rgba(204, 64, 73, 0.3)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <AddComment sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Growth Conversation Details" arrow placement="top">
                        <IconButton
                          onClick={() => handleViewGrowthSession(userActiveGrowthSession)}
                          size="small"
                          sx={{ 
                            padding: '6px',
                            backgroundColor: alpha('#17a2b8', 0.1),
                            color: '#17a2b8',
                            border: `1px solid ${alpha('#17a2b8', 0.3)}`,
                            borderRadius: '8px',
                            '&:hover': {
                              backgroundColor: alpha('#17a2b8', 0.2),
                              transform: 'scale(1.1)',
                              boxShadow: '0 2px 8px rgba(23, 162, 184, 0.3)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Create Performance Improvement Plan" arrow placement="top">
                        <IconButton
                          onClick={() => handleCreatePIPPlan(userActiveGrowthSession.id, params.row.email)}
                          size="small"
                          sx={{ 
                            padding: '6px',
                            backgroundColor: alpha('#ff9800', 0.1),
                            color: '#ff9800',
                            border: `1px solid ${alpha('#ff9800', 0.3)}`,
                            borderRadius: '8px',
                            '&:hover': {
                              backgroundColor: alpha('#ff9800', 0.2),
                              transform: 'scale(1.1)',
                              boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <AssignmentIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : hasLeadAssignedOnly || (userActiveGrowthSession && userActiveGrowthSession.status !== 'growth_session') ? (
                    // Show only growth session button when lead assigned exists (status = 'lead_assigned') or other non-feedback status
                    <Tooltip title="Create Growth Conversation Feedback" arrow placement="top">
                      <IconButton
                        onClick={() => handleOpenGrowthForm(params.row.email)}
                        size="small"
                        sx={{ 
                          padding: '6px',
                          backgroundColor: alpha('#cc4049', 0.1),
                          color: '#cc4049',
                          border: `1px solid ${alpha('#cc4049', 0.3)}`,
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: alpha('#cc4049', 0.2),
                            transform: 'scale(1.1)',
                            boxShadow: '0 2px 8px rgba(204, 64, 73, 0.3)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <AddComment sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    // User has completed growth session(s), show "Create New Growth Session" button
                    <Tooltip title="Create New Growth Conversation" arrow placement="top">
                      <IconButton
                        onClick={() => handleOpenGrowthForm(params.row.email, true)}
                        size="small"
                        sx={{ 
                          padding: '6px',
                          backgroundColor: alpha('#0a0068', 0.1),
                          color: '#0a0068',
                          border: `1px solid ${alpha('#0a0068', 0.3)}`,
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: alpha('#0a0068', 0.2),
                            transform: 'scale(1.1)',
                            boxShadow: '0 2px 8px rgba(10, 0, 104, 0.3)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )
                ) : null}

                {/* History icon - show for all users if manager/lead/mentor, only own profile if user */}
                {(canManageUsers() || (userRole === "User" && params.row.email === currentUser)) && (
                  <Tooltip title="View User History" arrow placement="top">
                    <IconButton
                      onClick={() => handleViewUserHistory(params.row.email, params.row.name)}
                      size="small"
                      sx={{ 
                        padding: '6px',
                        backgroundColor: alpha('#6c757d', 0.1),
                        color: '#6c757d',
                        border: `1px solid ${alpha('#6c757d', 0.3)}`,
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: alpha('#6c757d', 0.2),
                          transform: 'scale(1.1)',
                          boxShadow: '0 2px 8px rgba(108, 117, 125, 0.3)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <HistoryIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Stack>
        );
      }
    }
  ];

  // Custom toolbar removed - using CommonTable built-in search

  // Users Tab Content
  const UsersTabContent = () => {
    const activeFiltersCount = Object.values(userFilters).filter(v => v).length;

    return (
      <Box>
        {/* Statistics */}
        <Grid container spacing={2} sx={{ mt: 1.5, mb: 1.5, px: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Grow in timeout={500}>
              <Box sx={{ height: '100%' }}>
                <StatCard
                  title="Team Members"
                  value={filteredUsers.filter(user => user.role === 'User').length}
                  icon={<PeopleIcon />}
                  subtitle="Active users in the system"
                />
              </Box>
            </Grow>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Grow in timeout={700}>
              <Box sx={{ height: '100%' }}>
                <StatCard
                  title="Managers/Mentors"
                  value={filteredUsers.filter(user => user.role === 'Manager' || user.role === 'Mentor').length}
                  icon={<GroupsIcon />}
                  subtitle="Leadership and guidance roles"
                />
              </Box>
            </Grow>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Grow in timeout={900}>
              <Box sx={{ height: '100%' }}>
                <StatCard
                  title="Leads"
                  value={filteredUsers.filter(user => user.role === 'Lead').length}
                  icon={<TrendingUpIcon />}
                  subtitle="Project and team leads"
                />
              </Box>
            </Grow>
          </Grid>
        </Grid>

        {/* Header */}
        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          mb: 1,
          gap: 2
        }}>
          {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <PeopleIcon color="primary" />
            <Typography variant="h5" component="h2" fontWeight="600">
              Users Management
            </Typography>
            <Chip
              label={filteredUsers.length}
              color="primary"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
            {userBatch && (
              <Chip
                label={`Batch: ${userBatch}`}
                color="secondary"
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box> */}
        </Box>


        {/* Users Table */}
        <Box sx={{ width: '100%' }}>
          <UserTable
            tableData={filteredUsers}
            handleEditUser={(user) => handleCreateGrowthSession(user.id || user.email)}
            handleCreateSession={(user) => handleCreateGrowthSession(user.id || user.email)}
            isLoading={loading}
            actionButtons={
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  onClick={() => handleCreateGrowthSession()}
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: 1,
                    px: 2,
                    fontSize: '13px',
                    py: 0.75,
                    background: "linear-gradient(45deg, #0a0068 30%, #cc4049 90%)",
                    "&:hover": {
                      background: "linear-gradient(45deg, #1565c0 30%, #0a0068 90%)",
                    },
                  }}
                >
                  Create Session
                </Button>
              </Stack>
            }
          />
        </Box>
      </Box>
    );
  };

  // Growth Sessions Tab Content
  const GrowthSessionsTabContent = () => {

    // Calculate statistics based on user role
    const growthSessionStats = userRole === "User" ? {
      // User-specific statistics (based on their own sessions)
      mySessions: filteredGrowthSessions.length,
      completedSessions: filteredGrowthSessions.filter(s => 
        s.status === 'pip_created'
      ).length,
      inProgressSessions: filteredGrowthSessions.filter(s => 
        s.status !== 'pip_created'
      ).length,
      pipSessions: filteredGrowthSessions.filter(s => s.status === 'pip_created').length
    } : {
      // Management statistics (filtered sessions for Mentors/Leads, all sessions for Managers)
      assignedUsers: new Set(filteredGrowthSessions.map(s => s.user_id)).size,
      totalSessions: filteredGrowthSessions.filter(s => s.status === 'growth_session').length,
      pipsCreated: filteredGrowthSessions.filter(s => s.status === 'pip_created').length,
      activeSessions: filteredGrowthSessions.filter(s => 
        s.status !== 'pip_created'
      ).length
    };

    return (
      <Box>
        {/* Statistics - Different for Users vs Management */}
        {userRole === "User" ? (
          // User-specific statistics
          <Grid container spacing={2} sx={{ mt: 1.5, mb: 1.5, px: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in timeout={500}>
                <Box sx={{ height: '100%' }}>
                  <StatCard
                    title="My Sessions"
                    value={growthSessionStats.mySessions}
                    icon={<FeedbackIcon />}
                    subtitle="Total growth conversation sessions"
                  />
                </Box>
              </Grow>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in timeout={700}>
                <Box sx={{ height: '100%' }}>
                  <StatCard
                    title="Completed Reviews"
                    value={growthSessionStats.completedSessions}
                    icon={<CheckCircleIcon />}
                    subtitle="Successfully completed sessions"
                  />
                </Box>
              </Grow>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in timeout={900}>
                <Box sx={{ height: '100%' }}>
                  <StatCard
                    title="In Progress"
                    value={growthSessionStats.inProgressSessions}
                    icon={<TrendingUpIcon />}
                    subtitle="Sessions currently in progress"
                  />
                </Box>
              </Grow>
            </Grid>
            {growthSessionStats.pipSessions > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Grow in timeout={1100}>
                  <Box sx={{ height: '100%' }}>
                    <StatCard
                      title="Improvement Plans"
                      value={growthSessionStats.pipSessions}
                      icon={<AssignmentIcon />}
                      subtitle="Performance improvement plans"
                    />
                  </Box>
                </Grow>
              </Grid>
            )}
          </Grid>
        ) : (
          // Management statistics (original)
          <Grid container spacing={2} sx={{ mt: 1.5, mb: 1.5, px: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in timeout={500}>
                <Box sx={{ height: '100%' }}>
                  <StatCard
                    title="Assigned Team Members"
                    value={growthSessionStats.assignedUsers}
                    icon={<PeopleIcon />}
                    subtitle="Team members under management"
                  />
                </Box>
              </Grow>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in timeout={700}>
                <Box sx={{ height: '100%' }}>
                  <StatCard
                    title="Growth Conversations Created"
                    value={growthSessionStats.totalSessions}
                    icon={<FeedbackIcon />}
                    subtitle="Total sessions created this month"
                  />
                </Box>
              </Grow>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in timeout={900}>
                <Box sx={{ height: '100%' }}>
                  <StatCard
                    title="PIPs Created"
                    value={growthSessionStats.pipsCreated}
                    icon={<AssignmentIcon />}
                    subtitle="Performance improvement plans created"
                  />
                </Box>
              </Grow>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in timeout={1100}>
                <Box sx={{ height: '100%' }}>
                  <StatCard
                    title="Active Sessions"
                    value={growthSessionStats.activeSessions}
                    icon={<TrendingUpIcon />}
                    subtitle="Sessions currently active"
                  />
                </Box>
              </Grow>
            </Grid>
          </Grid>
        )}


        {/* Growth Sessions Table */}
        <Box sx={{ width: '100%' }}>
          <GrowthSessionTable
            tableData={filteredGrowthSessions}
            handleEdit={(session) => {
              setEditingGrowthSession(session);
              setOpenGrowthSessionDialog(true);
            }}
            handleDelete={async (sessionId) => {
              try {
                await deleteGrowthSessionAPI(sessionId);
                showSnackbar('Growth session deleted successfully', 'success');
                await loadGrowthSessions();
              } catch (error) {
                console.error('Error deleting growth session:', error);
                showSnackbar('Error deleting growth session', 'error');
              }
            }}
            handleView={handleViewGrowthSession}
            handleFeedback={(session) => {
              // Open feedback/view dialog
              handleViewGrowthSession(session);
            }}
            isLoading={loading}
            actionButtons={
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  onClick={() => handleOpenGrowthForm()}
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: 1,
                    px: 2,
                    fontSize: '13px',
                    py: 0.75,
                    background: "linear-gradient(45deg, #0a0068 30%, #cc4049 90%)",
                    "&:hover": {
                      background: "linear-gradient(45deg, #1565c0 30%, #0a0068 90%)",
                    },
                  }}
                >
                  New Session
                </Button>
              </Stack>
            }
          />
        </Box>
      </Box>
    );
  };

  // Render tab content based on active tab
  const renderTabContent = (activeTabId) => {
    switch (activeTabId) {
      case 'users':
        return <UsersTabContent />;
      case 'growth-sessions':
        return <GrowthSessionsTabContent />;
      default:
        return canManageUsers() ? <UsersTabContent /> : <GrowthSessionsTabContent />;
    }
  };

  // Show loading screen during initial data load or when essential data is missing
  if (loading || !userRole || (userRole !== "User" && users.length === 0)) {
    return (
      <AppContext.Provider value={contextValue}>
        <LoadingScreen message="Loading Growth Conversations..." />
      </AppContext.Provider>
    );
  }

  return (
    <AppContext.Provider value={contextValue}>
      <div style={{ background: 'white', minHeight: '100vh' }}>
        <TabsLayout
          tabs={getTabs()}
          title="Growth Conversations"
          defaultTab={canManageUsers() ? 'users' : 'growth-sessions'}
          onTabChange={handleTabChange}
          className="h-screen"
        >
          {renderTabContent}
        </TabsLayout>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          PaperProps={{
            sx: {
              borderRadius: 2,
              mt: 1,
              boxShadow: theme.shadows[8],
              minWidth: 200,
              '& .MuiMenuItem-root': {
                fontSize: '0.875rem',
                mx: 0.5,
                my: 0.25
              }
            }
          }}
        >
          <MenuItem onClick={() => {
            if (selectedItem && selectedItem.status) {
              handleOpenGrowthForm(selectedItem.user_id, false, selectedItem);
              handleCloseMenu();
            }
          }}>
            <ListItemIcon>
              <AddComment fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create/Update Growth Conversation</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            if (selectedItem) {
              if (selectedItem.status) {
                deleteGrowthSessionLocal(selectedItem.id);
              } else {
                // deleteUser functionality removed
              }
            }
            handleCloseMenu();
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>

        {/* Dialogs */}
        <GrowthSessionDialog
          open={openGrowthSessionDialog}
          onClose={handleCloseGrowthSessionDialog}
          editingGrowthSession={editingGrowthSession}
          selectedUserId={selectedUserId}
          onSuccess={handleGrowthSessionSuccess}
          forceNew={forceNewSession}
        />

        <ViewGrowthSessionDialog
          open={openViewDialog}
          onClose={handleCloseViewDialog}
          viewingGrowthSession={viewingGrowthSession}
        />

        {/* Common History Dialog - used for feedback tab */}
        <CommonHistoryDialog
          open={openHistoryDialog}
          onClose={() => {
            setOpenHistoryDialog(false);
            setSelectedUserForHistory(null);
          }}
          selectedUser={selectedUserForHistory}
          title="Feedback History"
          type="feedback"
        />

        <PIPDialog
          open={openPIPDialog}
          onClose={handleClosePIPDialog}
          editingPIPPlan={editingPIPPlan}
          growthSessionId={selectedGrowthSessionId}
          userId={selectedUserId}
          onSuccess={handlePIPSuccess}
        />

        <UserHierarchyDialog
          open={openUserHierarchyDialog}
          onClose={handleCloseUserHierarchyDialog}
          editingHierarchy={editingHierarchy}
          onSuccess={handleHierarchySuccess}
          availableUsers={users}
          selectedUserId={selectedUserId}
        />

        {/* Common History Dialog - used for users tab */}
        <CommonHistoryDialog
          open={openUserHistoryDialog}
          onClose={handleCloseUserHistoryDialog}
          selectedUser={selectedUserForHistory}
          title="User History"
          type="growth_session"
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              width: '100%',
              boxShadow: theme.shadows[3]
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </AppContext.Provider>
  );
};

export default GrowthSessionManagement;
